import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Maximize2, Minimize2, Play } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";

export const MiniPlayer = () => {
  const { t } = useI18n();
  const { player, setPlayer, consent, setConsent } = useSite();
  const [mode, setMode] = useState("mini");

  const close = () => {
    setPlayer(null);
    setMode("mini");
  };
  const short = player?.short;
  const sizes = {
    mini: short ? "w-[200px] sm:w-[230px]" : "w-[calc(100vw-24px)] sm:w-[380px]",
    big: short ? "w-[min(86vw,360px)]" : "w-[min(94vw,980px)]",
  };

  return (
    <AnimatePresence>
      {player && (
        <motion.div
          key="mini-player"
          initial={{ opacity: 0, y: 60, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 60, scale: 0.9 }}
          transition={{ type: "spring", stiffness: 260, damping: 24 }}
          className={`fixed z-[60] ${mode === "big" ? "inset-0 grid place-items-center p-3 pointer-events-none" : "right-3 bottom-3 sm:right-6 sm:bottom-6"}`}
        >
          <div
            className={`${sizes[mode === "big" ? "big" : "mini"]} max-w-full rounded-[22px] sticker bg-ink overflow-hidden ${mode === "big" ? "pointer-events-auto" : ""}`}
            data-testid="mini-player"
            data-lenis-prevent
          >
          <div className="flex items-center gap-2 px-3 py-2 bg-sun text-ink">
            <span className="w-2 h-2 rounded-full bg-pink animate-pulse shrink-0" />
            <p className="text-xs font-extrabold truncate flex-1" data-testid="mini-player-title">{mode === "min" ? t.video.now : player.title}</p>
            <button onClick={() => setMode(mode === "min" ? "mini" : "min")} aria-label={t.common.minimize} className="p-1 rounded-full hover:bg-ink/10" data-testid="mini-player-minimize"><Minus className="w-4 h-4" /></button>
            <button onClick={() => setMode(mode === "big" ? "mini" : "big")} aria-label={t.common.expand} className="p-1 rounded-full hover:bg-ink/10 hidden sm:block" data-testid="mini-player-expand">
              {mode === "big" ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={close} aria-label={t.common.close} className="p-1 rounded-full hover:bg-ink/10" data-testid="mini-player-close"><X className="w-4 h-4" /></button>
          </div>
          <div className={mode === "min" ? "hidden" : `relative ${short ? "aspect-[9/16]" : "aspect-video"}`}>
            {consent === "all" ? (
              <iframe
                title={player.title}
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube-nocookie.com/embed/${player.id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
                data-testid="mini-player-iframe"
              />
            ) : (
              <div className="absolute inset-0 grid place-items-center p-5 text-center text-white">
                <img src={`https://i.ytimg.com/vi/${player.id}/hqdefault.jpg`} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
                <div className="relative">
                  <p className="text-sm font-bold">{t.video.consent}</p>
                  <button onClick={() => setConsent("all")} className="mt-3 inline-flex items-center gap-2 rounded-full bg-pink px-4 py-2 font-display font-semibold border-2 border-white" data-testid="mini-player-allow-btn">
                    <Play className="w-4 h-4 fill-white" /> {t.video.allow}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
